<template>
  <div class="wrapper">
    <admin-title :title="$route.meta.name"></admin-title>
    <!-- 标签宽度需与组卡片的关联策略标签一致。 -->
    <el-form
      ref="form"
      :model="formData"
      :rules="rules"
      label-width="130px"
      :disabled="readonly"
    >
      <el-form-item label="规则名称" prop="ruleName">
        <el-input
          v-model="formData.ruleName"
          style="width: 400px"
          size="small"
          placeholder="请输入规则名称"
          maxlength="20"
          show-word-limit
          clearable
        ></el-input>
      </el-form-item>

      <!-- 编辑时禁止切换类型，避免清空已有规则组。 -->
      <el-form-item label="规则类型" prop="ruleType">
        <el-select
          v-model="formData.ruleType"
          style="width: 400px"
          size="small"
          placeholder="请选择规则类型"
          clearable
          :disabled="isEdit"
          @change="onRuleTypeChange"
        >
          <el-option
            v-for="item in MachineRuleTypeList"
            :key="item.value"
            :label="item.name"
            :value="item.value"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item v-if="isBase" ref="prepayRateFormItem" label="命中首付比" prop="prepayRate">
        <div class="input-with-suffix input-with-suffix--prepay">
          <el-input-number
            v-model="formData.prepayRate"
            :controls="false"
            :min="20"
            :max="60"
            :precision="0"
            placeholder="请输入首付比"
            size="small"
            @change="validatePrepayRate"
            @blur="validatePrepayRate"
          />
          <span class="suffix">%</span>
        </div>
      </el-form-item>

      <el-form-item v-if="isCashOut" label="锚点首付比" prop="anchorPrepayRate">
        <el-select
          v-model="formData.anchorPrepayRate"
          style="width: 400px"
          size="small"
          :loading="anchorLoading"
          :placeholder="
            !anchorLoading && !anchorOptions.length
              ? '暂无可用锚点，请先启用基准规则'
              : '请选择锚点首付比'
          "
          empty-text="暂无可用锚点，请先启用基准规则"
          clearable
        >
          <el-option
            v-for="item in anchorOptions"
            :key="item.value"
            :label="item.name"
            :value="item.value"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item v-if="isManual" label="标签类目" prop="tagCategory">
        <el-input
          v-model="formData.tagCategory"
          style="width: 400px"
          size="small"
          placeholder="请输入标签类目"
          maxlength="10"
          clearable
        ></el-input>
      </el-form-item>

      <el-form-item
        :label="readonly ? (isManual ? '标签配置' : '规则配置') : ''"
        :class="{ 'rule-config-readonly': readonly }"
        label-width="130px"
        prop="ruleGroupList"
        :rules="ruleGroupRules"
      >
        <div ref="ruleConfigAnchor" class="rule-config-anchor"></div>
        <div
          v-if="!readonly"
          class="rule-config-toolbar"
          :class="{ 'is-sticky': ruleConfigSticky }"
        >
          <span class="rule-config-label">{{
            isManual ? "标签配置" : "规则配置"
          }}</span>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-plus"
            @click="addGroup"
            >{{ isManual ? "添加标签组" : "添加规则组" }}</el-button
          >
        </div>
        <div class="rule-group-empty" v-if="!formData.ruleGroupList.length">
          <el-empty description="暂无数据" :image-size="120"></el-empty>
        </div>
        <rule-group-card
          v-for="(group, index) in formData.ruleGroupList"
          :key="group._key"
          :class="{ 'rule-group-card--first': readonly && index === 0 }"
          :group="group"
          :index="index"
          :rule-type="formData.ruleType"
          :readonly="readonly"
          @remove="removeGroup(index)"
          @add-strategy="openStrategyDialog(index)"
        />
      </el-form-item>
      <el-form-item label="">
        <!-- 显式启用返回按钮，避免继承详情表单的禁用状态。 -->
        <el-button size="small" :disabled="false" @click="goToList">{{
          readonly ? "返回" : "取消"
        }}</el-button>
        <el-button
          v-if="!readonly"
          type="primary"
          size="small"
          :disabled="waitingDetail"
          :loading="saving"
          @click="save"
          >保存</el-button
        >
      </el-form-item>
    </el-form>

    <strategy-select-dialog
      v-if="!readonly"
      :visible.sync="strategyDialogVisible"
      :picked-codes="dialogPickedCodes"
      @confirm="onStrategyConfirmed"
    />

    <div style="height: 50px"></div>
  </div>
</template>

<script>
import {
  EnumMachineRuleType,
  MachineRuleTypeList,
  hasAdjustFields,
} from "@/enum/dict/machine-rule.js";
import {
  createMachineRuleAPI,
  editMachineRuleAPI,
  getMachineRuleDetailAPI,
  getMachineRuleAnchorListAPI,
} from "./api";
import RuleGroupCard from "./modules/rule-group-card.vue";
import StrategySelectDialog from "./modules/strategy-select-dialog.vue";
import { getStrategyHits } from "./modules/strategy-validation";

const createEmptyForm = () => ({
  id: undefined,
  ruleName: "",
  ruleType: undefined,
  // 编辑时保留后端排序值，新增时使用后端默认值。
  priority: undefined,
  prepayRate: undefined,
  anchorPrepayRate: undefined,
  tagCategory: "",
  ruleGroupList: [],
});

const checkRuleGroup = (unitName) => (rule, value, callback) => {
  if (!value || !value.length) {
    callback(new Error(`请至少添加一个${unitName}`));
  } else {
    callback();
  }
};

export default {
  name: "ReviewMachineRuleAdd",

  components: { RuleGroupCard, StrategySelectDialog },

  data() {
    return {
      MachineRuleTypeList,
      anchorOptions: [],
      anchorLoading: false,
      lastRuleType: undefined,
      // 使用稳定的组 key，避免删除后复用错误的控件状态。
      groupSeq: 0,
      strategyDialogVisible: false,
      strategyDialogGroupIndex: -1,
      detailLoaded: false,
      formVersion: 0,
      saving: false,
      ruleConfigSticky: false,
      formData: createEmptyForm(),
      rules: {
        ruleName: [
          {
            required: true,
            whitespace: true,
            message: "规则名称不能为空",
            trigger: "blur",
          },
        ],
        ruleType: [
          { required: true, message: "请选择规则类型", trigger: "change" },
        ],
        prepayRate: [
          { required: true, message: "请输入命中首付比", trigger: "change" },
        ],
        anchorPrepayRate: [
          { required: true, message: "请选择锚点首付比", trigger: "change" },
        ],
        tagCategory: [
          {
            required: true,
            whitespace: true,
            message: "请输入标签类目",
            trigger: "blur",
          },
        ],
      },
    };
  },

  watch: {
    $route: "loadRoute",
    "formData.ruleType"() {
      this.$nextTick(() => {
        this.$refs.form &&
          this.$refs.form.clearValidate([
            "ruleGroupList",
            "prepayRate",
            "anchorPrepayRate",
            "tagCategory",
          ]);
      });
    },
  },

  mounted() {
    const anchor = this.$refs.ruleConfigAnchor;
    this.ruleConfigObserver = new IntersectionObserver(
      ([entry]) => {
        this.ruleConfigSticky =
          !entry.isIntersecting &&
          entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0);
      },
      { root: anchor.closest(".ant-layout-content") },
    );
    this.ruleConfigObserver.observe(anchor);
    this.getAnchorList();
    this.loadRoute(this.$route);
  },

  beforeDestroy() {
    this.ruleConfigObserver && this.ruleConfigObserver.disconnect();
    this.formVersion++;
  },

  computed: {
    // 详情也有 id，只读状态由路由元信息决定。
    readonly() {
      return !!this.$route.meta.readonly;
    },
    // 详情回填成功前禁止提交编辑。
    waitingDetail() {
      return (
        this.isEdit &&
        (!this.detailLoaded ||
          String(this.formData.id) !== String(this.$route.params.id))
      );
    },
    isEdit() {
      return !!this.$route.params.id;
    },
    isCashOut() {
      return this.formData.ruleType === EnumMachineRuleType.CASH_OUT;
    },
    isBase() {
      return this.formData.ruleType === EnumMachineRuleType.BASE;
    },
    isManual() {
      return this.formData.ruleType === EnumMachineRuleType.MANUAL;
    },
    hasAdjust() {
      return hasAdjustFields(this.formData.ruleType);
    },
    hasPrepayRate() {
      const v = this.formData.prepayRate;
      return v !== undefined && v !== null && v !== "";
    },
    hasAnchorPrepayRate() {
      const v = this.formData.anchorPrepayRate;
      return v !== undefined && v !== null && v !== "";
    },
    hasTagCategory() {
      return !!this.formData.tagCategory;
    },
    ruleGroupRules() {
      return [
        {
          validator: checkRuleGroup(this.isManual ? "标签组" : "规则组"),
          trigger: "change",
        },
      ];
    },
    hasConfigToClear() {
      if (this.formData.ruleGroupList.length > 0) return true;
      return (
        this.hasPrepayRate || this.hasAnchorPrepayRate || this.hasTagCategory
      );
    },
    dialogPickedCodes() {
      const group = this.formData.ruleGroupList[this.strategyDialogGroupIndex];
      if (!group) return [];
      return group.strategyConfig.map((row) => row.strategyCode);
    },
  },

  methods: {
    // 数值组件提交输入后，等待绑定值更新再刷新校验结果。
    validatePrepayRate() {
      this.$nextTick(() => {
        const field = this.$refs.prepayRateFormItem;
        if (field) field.validate("", () => {});
      });
    },
    loadRoute(route) {
      const version = ++this.formVersion;
      this.formData = createEmptyForm();
      this.detailLoaded = false;
      this.saving = false;
      this.lastRuleType = undefined;
      this.strategyDialogVisible = false;
      this.strategyDialogGroupIndex = -1;
      this.$nextTick(() => {
        if (version === this.formVersion && this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      });
      if (route.params.id) this.getDetail(route.params.id, version);
    },
    async getAnchorList() {
      this.anchorLoading = true;
      try {
        const res = await getMachineRuleAnchorListAPI();
        this.anchorOptions = (res || []).map((item) => ({
          name: item.enumText,
          value: Number(item.enumCode),
        }));
      } catch {
        // 请求错误由 http 统一提示。
      } finally {
        this.anchorLoading = false;
      }
    },
    async getDetail(id, version) {
      try {
        const res = await getMachineRuleDetailAPI(id);
        // 路由切换后忽略旧详情，回填 id 必须与当前规则一致。
        if (
          version !== this.formVersion ||
          String(id) !== String(this.$route.params.id) ||
          !res ||
          String(res.id) !== String(id)
        ) {
          return;
        }
        this.fillForm(res);
        this.detailLoaded = true;
      } catch {
        // 请求错误由 http 统一提示，回填失败时继续禁止保存。
      }
    },
    fillForm(res) {
      this.formData.id = res.id;
      this.formData.ruleName = res.ruleName;
      this.formData.ruleType = Number(res.ruleType);
      this.formData.prepayRate = res.prepayRate;
      this.formData.anchorPrepayRate =
        res.anchorPrepayRate === undefined ||
        res.anchorPrepayRate === null ||
        res.anchorPrepayRate === ""
          ? undefined
          : Number(res.anchorPrepayRate);
      this.formData.tagCategory = res.tagCategory || "";
      this.formData.priority = res.priority;
      this.formData.ruleGroupList = (res.ruleGroupList || []).map((group) =>
        this.buildGroup(group)
      );
    },
    buildGroup(group) {
      return {
        _key: this.groupSeq++,
        id: group.id,
        groupCode: group.groupCode,
        status: group.status,
        hitRemark: group.hitRemark,
        tagName: group.tagName || "",
        adjustDirection: this.adjustDirectionOf(group),
        adjustValue: this.adjustValueOf(group),
        strategyConfig: (group.strategyConfig || []).map((row) =>
          this.buildStrategyRow({ ...row, ...row.strategy })
        ),
      };
    },
    // 仅当方向缺失时，根据带符号的 adjustRate 推导。
    adjustDirectionOf(group) {
      const direction = Number(group.adjustDirection);
      if (direction === 1 || direction === -1) return direction;
      const rate = Number(group.adjustRate);
      if (rate > 0) return 1;
      if (rate < 0) return -1;
      return undefined;
    },
    adjustValueOf(group) {
      const value = group.adjustValue;
      if (value !== undefined && value !== null && value !== "") return value;
      const rate = Number(group.adjustRate);
      return rate ? Math.abs(rate) : undefined;
    },
    save() {
      if (this.readonly || this.waitingDetail) return;
      // 校验前锁定，防止快速连点重复提交。
      if (this.saving) return;
      const version = this.formVersion;
      const routePath = this.$route.fullPath;
      const isCurrent = () =>
        version === this.formVersion && routePath === this.$route.fullPath;
      const isEdit = this.isEdit;
      this.saving = true;
      this.$refs.form.validate((valid) => {
        if (!isCurrent()) return;
        if (!valid) {
          this.saving = false;
          this.scrollToFirstError();
          return;
        }
        const request = isEdit ? editMachineRuleAPI : createMachineRuleAPI;
        request(this.buildPayload())
          .then(() => {
            if (!isCurrent()) return;
            this.$message.success(isEdit ? "编辑成功" : "新增成功");
            this.goToList();
          })
          .catch(() => {
            if (isCurrent()) this.saving = false;
          });
      });
    },
    goToList() {
      this.$router.push({ name: "ReviewMachineRuleList" });
    },
    // 新增、编辑共用提交结构；仅保留接口字段并清理文本首尾空格。
    buildPayload() {
      return {
        id: this.formData.id,
        priority: this.formData.priority,
        ruleName: this.formData.ruleName.trim(),
        ruleType: this.formData.ruleType,
        prepayRate: this.isBase ? this.formData.prepayRate : undefined,
        anchorPrepayRate: this.isCashOut
          ? this.formData.anchorPrepayRate
          : undefined,
        tagCategory: this.isManual ? this.formData.tagCategory.trim() : undefined,
        ruleGroupList: this.formData.ruleGroupList.map((group, index) => ({
          id: group.id,
          groupCode: group.groupCode,
          status: group.status,
          hitRemark: (group.hitRemark || "").trim(),
          // 规则组按当前数组顺序判断。
          priority: index,
          tagName: this.isManual ? group.tagName.trim() : undefined,
          adjustDirection: this.hasAdjust ? group.adjustDirection : undefined,
          adjustValue: this.hasAdjust ? group.adjustValue : undefined,
          strategyConfig: group.strategyConfig.map((row) => ({
            strategyCode: row.strategyCode,
            operator: row.operator,
            hits: getStrategyHits(row),
          })),
        })),
      };
    },
    // 校验失败后定位首个错误字段或策略行。
    scrollToFirstError() {
      this.$nextTick(() => {
        const form = this.$refs.form;
        const field = form && form.$el.querySelector(".el-form-item.is-error");
        if (!field) return;
        const target = field.querySelector(".strategy-row.is-missing") || field;
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },
    addGroup() {
      this.formData.ruleGroupList.push({
        _key: this.groupSeq++,
        status: true,
        hitRemark: "",
        tagName: "",
        adjustDirection: undefined,
        adjustValue: undefined,
        strategyConfig: [],
      });
    },
    async removeGroup(index) {
      const version = this.formVersion;
      try {
        await this.$confirm(
          `删除第 ${index + 1} 个${
            this.isManual ? "标签组" : "规则组"
          }？组内已配置的策略会一并删除。`,
          "提示",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }
        );
      } catch {
        return;
      }
      if (version !== this.formVersion) return;
      this.formData.ruleGroupList.splice(index, 1);
    },
    openStrategyDialog(index) {
      this.strategyDialogGroupIndex = index;
      this.strategyDialogVisible = true;
    },
    onStrategyConfirmed(rows) {
      const group = this.formData.ruleGroupList[this.strategyDialogGroupIndex];
      if (!group) return;
      rows.forEach((row) => {
        group.strategyConfig.push(this.buildStrategyRow(row));
      });
    },
    // 策略选择与详情回显统一为扁平行；elType 3 沿用 hits=null。
    buildStrategyRow(row) {
      const elType = Number(row.elType);
      const hasOperator = row.operator !== undefined && row.operator !== null;
      const hasHits = row.hits !== undefined && row.hits !== null;
      return {
        strategyCode: row.strategyCode,
        operator: hasOperator ? row.operator : elType === 6 ? "in" : "",
        hits: elType === 3 ? null : hasHits ? row.hits : [],
        strategyName: row.strategyName,
        elType: row.elType,
        valueType: row.valueType,
        elData: row.elData,
        operatorList: row.operatorList,
      };
    },
    onRuleTypeChange(val) {
      if (!this.hasConfigToClear) {
        this.lastRuleType = val;
        return;
      }

      const version = this.formVersion;
      this.$confirm("切换规则类型将清空当前已填配置，是否继续？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          if (version !== this.formVersion) return;
          this.clearTypeConfig();
          this.lastRuleType = val;
        })
        .catch(() => {
          if (version !== this.formVersion) return;
          this.formData.ruleType = this.lastRuleType;
          this.$message({ type: "info", message: "已取消操作" });
        });
    },
    clearTypeConfig() {
      this.formData.ruleGroupList = [];
      this.formData.prepayRate = undefined;
      this.formData.anchorPrepayRate = undefined;
      this.formData.tagCategory = "";
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  width: 100%;

  /deep/ .el-form-item__content {
    line-height: 18px;
  }

  .rule-config-anchor {
    height: 1px;
    margin-bottom: -1px;
  }

  .rule-config-readonly {
    .rule-group-card--first,
    .rule-group-empty {
      margin-top: 0;
    }
  }

  .rule-config-toolbar {
    position: sticky;
    // 内容区有 24px 顶部内边距，吸顶时覆盖该留白，避免露出卡片。
    top: -24px;
    z-index: 10;
    display: flex;
    align-items: center;
    min-height: 56px;
    padding: 8px 0;
    margin-left: -130px;
    background: #fff;

    &.is-sticky {
      box-shadow: 0 1px 0 #ebeef5;
    }
  }

  .rule-config-label {
    flex: 0 0 130px;
    padding-right: 12px;
    text-align: right;
    font-size: 14px;
    line-height: 40px;
    color: #606266;
  }

  .input-with-suffix {
    display: flex;
    align-items: center;
    width: 400px;

    .el-input-number {
      flex: 1;
      min-width: 0;
      width: auto;
    }

    .suffix {
      flex: 0 0 auto;
      margin-left: 8px;
      color: #909399;
      white-space: nowrap;
    }

    // 输入框与上方字段同宽，百分号放在框外。
    &--prepay .el-input-number {
      flex: 0 0 400px;
      width: 400px;
    }
  }

  // 空态与规则组卡片保持同宽。
  .rule-group-empty {
    width: 1000px;
    height: 347px;
    margin-top: 16px;
    background: #fcfcfc;
    border-radius: 6px;
    border: 1px solid #dcdfe6;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
